import { useDebounceFnV2 } from '@dni-hooks/useDebounceFnV2'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiFastOrderItemsCheck } from '@hooks-api/fortune/use-api-fast-order-items-check'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { useEffect, useRef, useState } from 'react'
import { useStmMallCode } from '../../../hooks/use-stm-mall-code'
import { type CsvButlerItem, isCsvItemPending, isCsvItemQuantityInvalid, useStmQuickOrderStore } from '../../../hooks/use-stm-quick-order-store'
import { toCsvItemChecks, toFastOrderItemInputs, unavailableCsvItemCheck } from '../../utils/fast-order-transformer'

const RECHECK_DEBOUNCE_MS = 400

interface RunCheckOptions {
  /** 只重查這些品項；省略為全部未刪除品項 */
  keys?: ReadonlySet<CsvButlerItem['key']>
  markAllPending?: boolean
}

const toQuantityByKey = (items: CsvButlerItem[]) =>
  new Map(items.filter(item => !item.isDeleted).map(item => [item.key, item.quantity]))

/**
 * 掛載時檢查全部未刪除品項（含未勾選，它們也要有狀態與剩餘數量才能決定可否勾選）；改數量只重查被改的那幾筆。
 * 後端逐筆算「單價 × 數量」，列與列之間互不影響，所以部分重查拿到的結果跟整批一樣。
 * 請求序號按品項各自記：改 B 不會讓 A 在飛的回應作廢，但在飛請求回來時被改過的那筆不寫回，避免舊庫存蓋掉新結果。
 * 請求失敗時品項留在 pending，由 hasCheckFailed 讓頁面提供「重新檢查」出口；回應漏回的品項補成 stock_unavailable，避免單筆卡住整頁。
 */
export const useCsvItemsCheck = () => {
  const mallCode = useStmMallCode()
  const items = useStmQuickOrderStore(state => state.csv.items)
  const setCsvItemChecks = useStmQuickOrderStore(state => state.setCsvItemChecks)
  const { trigger } = useApiFastOrderItemsCheck({ mall_code: mallCode })
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()
  const sequenceCounterRef = useRef(0)
  const sequenceByKeyRef = useRef(new Map<CsvButlerItem['key'], number>())
  const [hasCheckFailed, setHasCheckFailed] = useState(false)

  const runCheck = useCallbackEvent(async ({ keys, markAllPending = true }: RunCheckOptions = {}) => {
    const targets = useStmQuickOrderStore.getState().csv.items.filter(item => !item.isDeleted && !isCsvItemQuantityInvalid(item) && (!keys || keys.has(item.key)))
    if (!targets.length) return
    const sequence = ++sequenceCounterRef.current
    targets.forEach(item => sequenceByKeyRef.current.set(item.key, sequence))
    const isCurrent = (key: CsvButlerItem['key']) => sequenceByKeyRef.current.get(key) === sequence
    if (markAllPending) setCsvItemChecks(Object.fromEntries(targets.map(item => [item.key, null])))
    setHasCheckFailed(false)
    try {
      const { data: { data: result } } = await trigger({ items: toFastOrderItemInputs(targets) })
      const checks = toCsvItemChecks(result.items)
      targets.forEach((item) => { checks[item.key] ??= unavailableCsvItemCheck })
      const currentChecks = Object.fromEntries(Object.entries(checks).filter(([key]) => isCurrent(key)))
      if (Object.keys(currentChecks).length) setCsvItemChecks(currentChecks)
    }
    catch {
      if (!targets.some(item => isCurrent(item.key))) return
      setHasCheckFailed(true)
      openSnackbar({ message: '庫存檢查失敗，請再試一次', severity: 'error' })
    }
  })

  const retryCheck = useCallbackEvent(() => {
    const pendingKeys = new Set(useStmQuickOrderStore.getState().csv.items.filter(item => !item.isDeleted && isCsvItemPending(item)).map(item => item.key))
    runCheck({ keys: pendingKeys, markAllPending: false })
  })

  const changedKeysRef = useRef(new Set<CsvButlerItem['key']>())
  const debouncedRecheckChanged = useDebounceFnV2(() => {
    const keys = new Set(changedKeysRef.current)
    changedKeysRef.current.clear()
    runCheck({ keys, markAllPending: false })
  }, RECHECK_DEBOUNCE_MS)

  const previousQuantityByKeyRef = useRef<Map<CsvButlerItem['key'], number> | null>(null)
  useEffect(function recheckOnMountAndQuantityChange() {
    const previous = previousQuantityByKeyRef.current
    const current = toQuantityByKey(items)
    previousQuantityByKeyRef.current = current
    // StrictMode 會把 effect 跑兩次，第二次 previous 已有值且數量沒變，不會重打
    if (previous === null) {
      runCheck()
      return
    }
    const changedKeys = [...current].filter(([key, quantity]) => previous.get(key) !== quantity).map(([key]) => key)
    if (!changedKeys.length) return
    // 數量已變，在飛的舊請求回來也不能寫回這幾筆，先讓它們失效再等 debounce 重送
    changedKeys.forEach((key) => {
      sequenceByKeyRef.current.set(key, ++sequenceCounterRef.current)
      changedKeysRef.current.add(key)
    })
    debouncedRecheckChanged()
  }, [items, runCheck, debouncedRecheckChanged])

  const hasPendingItems = items.some(item => !item.isDeleted && isCsvItemPending(item))

  return { hasPendingItems, hasCheckFailed, runCheck, retryCheck }
}
